import {
  Category,
  CategoryAvailability,
  Product,
  ProductFull,
  ProductType,
  Addon,
  Selection,
} from "../types/menu";

import apiClient from "./api";

class MenuService {
  async getProductTypeAddons(productTypeId: number): Promise<Addon[]> {
    try {
      const response:Addon[] = await apiClient.get(
        `/menu/product-types/${productTypeId}/addons`,
      );

      return response;
    } catch (error) {
      console.error("Error fetching product type addons:", error);
      throw error;
    }
  }

  async getAddonsByProductType(productTypeId: number): Promise<Addon[]> {
    return await apiClient.get<Addon[]>(
      `/menu/product-types/${productTypeId}/addons`,
    );
  }
  /**
   * Get all menu categories
   */
  async getCategories(
    params: {
      skip?: number;
      limit?: number;
      include_hidden?: boolean;
    } = {},
  ): Promise<Category[]> {
    return await apiClient.get<Category[]>("/menu/categories", { params });
  }

  /**
   * Create a new category
   */
  async createCategory(categoryData: {
    name: string;
    description?: string;
    position?: number;
    hidden?: boolean;
  }): Promise<Category> {
    return await apiClient.post<Category>("/menu/categories", categoryData);
  }

  /**
   * Update an existing category
   */
  async updateCategory(
    id: number,
    categoryData: {
      name?: string;
      description?: string;
      position?: number;
      hidden?: boolean;
    },
  ): Promise<Category> {
    return await apiClient.put<Category>(
      `/menu/categories/${id}`,
      categoryData,
    );
  }

  /**
   * Delete a category
   */
  async deleteCategory(id: number): Promise<void> {
    return await apiClient.delete<void>(`/menu/categories/${id}`);
  }

  /**
   * Add category availability
   */
  async addCategoryAvailability(availabilityData: {
    category_id: number;
    day_of_week: string;
    start_time: string;
    end_time: string;
  }): Promise<CategoryAvailability> {
    return await apiClient.post<CategoryAvailability>(
      "/menu/categories/availability",
      availabilityData,
    );
  }

  /**
   * Delete category availability
   */
  async deleteCategoryAvailability(id: number): Promise<void> {
    return await apiClient.delete<void>(`/menu/categories/availability/${id}`);
  }

  /**
   * Get all products with optional filtering
   */
  async getProducts(
    params: {
      skip?: number;
      limit?: number;
      category_id?: number;
      search?: string;
      include_unavailable?: boolean;
    } = {},
  ): Promise<Product[]> {
    return await apiClient.get<Product[]>("/menu/products", { params });
  }

  /**
   * Get product by ID
   */
  async getProductById(id: number): Promise<ProductFull> {
    return await apiClient.get<ProductFull>(`/menu/products/${id}`);
  }

  /**
   * Create a new product
   */
  async createProduct(productData: {
    code: string;
    name: string;
    description?: string;
    price: number;
    image_url?: string;
    is_featured?: boolean;
    unavailable?: boolean;
    category_ids: number[];
  }): Promise<Product> {
    return await apiClient.post<Product>("/menu/products", productData);
  }

  /**
   * Update an existing product
   */
  async updateProduct(
    id: number,
    productData: {
      code?: string;
      name?: string;
      description?: string;
      price?: number;
      image_url?: string;
      is_featured?: boolean;
      unavailable?: boolean;
      category_ids?: number[];
    },
  ): Promise<Product> {
    return await apiClient.put<Product>(`/menu/products/${id}`, productData);
  }

  /**
   * Delete a product
   */
  async deleteProduct(id: number): Promise<void> {
    return await apiClient.delete<void>(`/menu/products/${id}`);
  }

  /**
   * Update product availability
   */
  async updateProductAvailability(
    id: number,
    availabilityData: {
      unavailable: boolean;
    },
  ): Promise<Product> {
    return await apiClient.patch<Product>(
      `/menu/products/${id}/availability`,
      availabilityData,
    );
  }

  /**
   * Get all product types for a specific product
   */
  async getProductTypes(productId: number): Promise<ProductType[]> {
    return await apiClient.get<ProductType[]>(
      `/menu/products/${productId}/types`,
    );
  }

  /**
   * Create a new product type
   */
  async createProductType(productTypeData: {
    name: string;
    price: number;
    product_id: number;
  }): Promise<ProductType> {
    return await apiClient.post<ProductType>(
      "/menu/product-types",
      productTypeData,
    );
  }

  /**
   * Update an existing product type
   */
  async updateProductType(
    id: number,
    productTypeData: {
      name?: string;
      price?: number;
    },
  ): Promise<ProductType> {
    return await apiClient.put<ProductType>(
      `/menu/product-types/${id}`,
      productTypeData,
    );
  }

  /**
   * Delete a product type
   */
  async deleteProductType(id: number): Promise<void> {
    return await apiClient.delete<void>(`/menu/product-types/${id}`);
  }

  /**
   * Get all menu sections (categories with products)
   */
  async getMenuSections(): Promise<any[]> {
    return await apiClient.get<any[]>("/menu/sections");
  }

  /**
   * Get all addons
   */
  async getAddons(
    params: {
      skip?: number;
      limit?: number;
    } = {},
  ): Promise<Addon[]> {
    return await apiClient.get<Addon[]>("/menu/addons", { params });
  }

  /**
   * Create a new addon
   */
  async createAddon(addonData: { name: string; kind: number }): Promise<Addon> {
    return await apiClient.post<Addon>("/menu/addons", addonData);
  }

  /**
   * Update an existing addon
   */
  async updateAddon(
    id: number,
    addonData: {
      name?: string;
      kind?: number;
    },
  ): Promise<Addon> {
    return await apiClient.put<Addon>(`/menu/addons/${id}`, addonData);
  }

  /**
   * Delete an addon
   */
  async deleteAddon(id: number): Promise<void> {
    return await apiClient.delete<void>(`/menu/addons/${id}`);
  }

  /**
   * Get all selections for a specific addon
   */
  async getSelections(addonId: number): Promise<Selection[]> {
    return await apiClient.get<Selection[]>(
      `/menu/addons/${addonId}/selections`,
    );
  }

  /**
   * Create a new selection
   */
  async createSelection(selectionData: {
    name: string;
    addon_id: number;
  }): Promise<Selection> {
    return await apiClient.post<Selection>("/menu/selections", selectionData);
  }

  /**
   * Create a new selection price
   */
  async createSelectionPrice(selectionPriceData: {
    selection_id: number;
    price: number;
    kind: number;
  }): Promise<any> {
    return await apiClient.post<any>(
      "/menu/selections/prices",
      selectionPriceData,
    );
  }

  /**
   * Add addon to product type
   */
  async addAddonToProductType(
    productTypeId: number,
    addonId: number,
  ): Promise<void> {
    return await apiClient.post<void>(
      `/menu/product-types/${productTypeId}/addons/${addonId}`,
    );
  }

  /**
   * Remove addon from product type
   */
  async removeAddonFromProductType(
    productTypeId: number,
    addonId: number,
  ): Promise<void> {
    return await apiClient.delete<void>(
      `/menu/product-types/${productTypeId}/addons/${addonId}`,
    );
  }
}

// Create singleton instance
export const menuService = new MenuService();
